import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from './constants';
import { ApiResponse, LoginCredentials, User } from '../types';

// エラーメッセージ定数
const ERROR_MESSAGES = {
  NETWORK_ERROR: 'ネットワークエラーが発生しました',
  SERVER_ERROR: 'サーバーエラーが発生しました',
  UNAUTHORIZED: '認証が必要です',
  FORBIDDEN: 'アクセス権限がありません',
  NOT_FOUND: 'リソースが見つかりません',
  VALIDATION_ERROR: '入力値にエラーがあります',
  LOGIN_FAILED: 'ログインに失敗しました',
  TOKEN_EXPIRED: 'セッションが期限切れです'
};

// Axios インスタンスの作成
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30秒
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター
api.interceptors.request.use(
  (config) => {
    // リクエストログ
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
      baseURL: config.baseURL,
      data: config.data,
      params: config.params
    });

    // 認証トークンを追加
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // レスポンスログ
    console.log(`[API Response] ${response.status}`, {
      url: response.config.url,
      data: response.data
    });
    
    return response;
  },
  (error) => {
    console.error('[API Response Error]', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data
    });

    // 認証エラーの場合、自動的にログアウト
    if (error.response?.status === 401) {
      // ✅ 修正: authStore に CustomEvent で通知
      // - localStorage クリア・リダイレクト・Zustand 状態リセットは
      //   authStore.ts の auth:unauthorized リスナーが一元管理
      // - 循環インポート（api.ts ↔ authStore.ts）を回避
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    
    return Promise.reject(error);
  }
);

// エラーハンドリング用ヘルパー関数
const handleError = (error: any): ApiResponse<any> => {
  console.error('[API Error Handler]', error);

  if (error.response) {
    // サーバーからのエラーレスポンス
    const status = error.response.status;
    const data = error.response.data;
    
    switch (status) {
      case 400:
        return {
          success: false,
          error: data.error || 'VALIDATION_ERROR',
          message: data.message || ERROR_MESSAGES.VALIDATION_ERROR
        };
      case 401:
        return {
          success: false,
          error: data.error || 'UNAUTHORIZED',
          message: data.message || ERROR_MESSAGES.UNAUTHORIZED
        };
      case 403:
        return {
          success: false,
          error: data.error || 'FORBIDDEN',
          message: data.message || ERROR_MESSAGES.FORBIDDEN
        };
      case 404:
        return {
          success: false,
          error: data.error || 'NOT_FOUND',
          message: data.message || ERROR_MESSAGES.NOT_FOUND
        };
      case 500:
        return {
          success: false,
          error: data.error || 'SERVER_ERROR',
          message: data.message || ERROR_MESSAGES.SERVER_ERROR
        };
      default:
        return {
          success: false,
          error: data.error || 'SERVER_ERROR',
          message: data.message || ERROR_MESSAGES.SERVER_ERROR
        };
    }
  } else if (error.request) {
    // ネットワークエラー
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: ERROR_MESSAGES.NETWORK_ERROR
    };
  } else {
    // その他のエラー
    return {
      success: false,
      error: 'UNKNOWN_ERROR',
      message: error.message || '予期しないエラーが発生しました'
    };
  }
};

// API関数群
export const apiClient = {
  // 基本的なHTTPメソッド
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await api.get(url, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleError(error);
    }
  },

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await api.post(url, data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleError(error);
    }
  },

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await api.put(url, data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleError(error);
    }
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await api.delete(url, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleError(error);
    }
  },

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await api.patch(url, data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleError(error);
    }
  }
};

// ===================================
// 認証関連API
// ===================================

export const authAPI = {
  /**
   * ログイン
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; accessToken: string; refreshToken: string }>> {
    console.log('[Auth API] Login attempt', { username: credentials.username });
    
    try {
      const response = await api.post('/auth/login', {
        username: credentials.username,
        password: credentials.password
      });

      console.log('[Auth API] Login response received', response.data);

      // レスポンス構造の確認と正規化
      if (response.data.success && response.data.data) {
        const { user, accessToken, refreshToken } = response.data.data;
        
        return {
          success: true,
          data: {
            user,
            accessToken,
            refreshToken
          },
          message: response.data.message || 'ログインに成功しました'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'LOGIN_FAILED',
          message: response.data.message || ERROR_MESSAGES.LOGIN_FAILED
        };
      }
    } catch (error: any) {
      console.error('[Auth API] Login error', error);
      return handleError(error);
    }
  },

  /**
   * ログアウト
   */
  async logout(): Promise<ApiResponse<void>> {
    console.log('[Auth API] Logout attempt');
    
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const response = await api.post('/auth/logout', { refreshToken });
      
      return {
        success: true,
        data: undefined,
        message: response.data.message || 'ログアウトしました'
      };
    } catch (error: any) {
      console.error('[Auth API] Logout error', error);
      return handleError(error);
    }
  },

  /**
   * 現在のユーザー情報取得
   */
  async getProfile(): Promise<ApiResponse<User>> {
    console.log('[Auth API] Get profile attempt');
    
    try {
      const response = await api.get('/auth/me');
      
      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data.user,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'USER_FETCH_ERROR',
          message: response.data.message || 'ユーザー情報の取得に失敗しました'
        };
      }
    } catch (error: any) {
      console.error('[Auth API] Get profile error', error);
      return handleError(error);
    }
  },

  /**
   * トークンリフレッシュ
   */
  async refreshToken(): Promise<ApiResponse<{ accessToken: string; refreshToken?: string }>> {
    console.log('[Auth API] Token refresh attempt');
    
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        return {
          success: false,
          error: 'NO_REFRESH_TOKEN',
          message: 'リフレッシュトークンがありません'
        };
      }

      const response = await api.post('/auth/refresh', { refreshToken });
      
      if (response.data.success && response.data.data) {
        const newAccessToken = response.data.data.accessToken;
        const newRefreshToken = response.data.data.refreshToken;
        
        // トークンを更新
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        
        return {
          success: true,
          data: {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken
          },
          message: response.data.message || 'トークンをリフレッシュしました'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'TOKEN_REFRESH_FAILED',
          message: response.data.message || 'トークンリフレッシュに失敗しました'
        };
      }
    } catch (error: any) {
      console.error('[Auth API] Token refresh error', error);
      return handleError(error);
    }
  },

  /**
   * パスワード変更
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    console.log('[Auth API] Password change attempt');
    
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      return {
        success: true,
        data: undefined,
        message: response.data.message || 'パスワードを変更しました'
      };
    } catch (error: any) {
      console.error('[Auth API] Password change error', error);
      return handleError(error);
    }
  }
};

// ===================================
// ユーザー関連API
// ===================================

export const userAPI = {
  /**
   * ユーザー一覧取得
   * 
   * ✅✅✅ 修正: バックエンドのレスポンス構造に合わせて型定義を修正
   * バックエンドレスポンス: { success: true, data: { users: [...], pagination: {...} } }
   * 
   * ✅✅✅ 修正追加: pageSize → limit 変換処理を追加（バックエンド互換性）
   */
  async getUsers(params?: { 
    page?: number; 
    limit?: number; 
    pageSize?: number;
    search?: string;
    role?: string;
    status?: string;
  }): Promise<ApiResponse<{ 
    users: User[]; 
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }
  }>> {
    console.log('[User API] Get users attempt', params);
    
    // ✅ 修正: pageSizeをlimitに変換（バックエンド互換性のため）
    // バックエンドは limit パラメータを期待しているが、
    // フロントエンドのuserStoreは pageSize を送信するため変換が必要
    const apiParams: any = params ? { ...params } : {};
    // pageSize → limit 変換（バックエンド互換）
    if (apiParams.pageSize && !apiParams.limit) {
      apiParams.limit = apiParams.pageSize;
      delete apiParams.pageSize;
    }
    // searchTerm → search 変換（バックエンドは 'search' を期待）
    if (apiParams.searchTerm !== undefined) {
      apiParams.search = apiParams.searchTerm;
      delete apiParams.searchTerm;
    }
    // isActive(boolean) → status('active'|'inactive') 変換
    // バックエンドコントローラーは status パラメータで受け取る
    if (apiParams.isActive !== undefined && apiParams.isActive !== '') {
      apiParams.status = apiParams.isActive === true || apiParams.isActive === 'true'
        ? 'active' : 'inactive';
      delete apiParams.isActive;
    }

    console.log('[User API] Converted params:', apiParams);
    
    return apiClient.get('/users', { params: apiParams });
  },

  /**
   * ユーザー作成
   */
  async createUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.post('/users', userData);
  },

  /**
   * ユーザー更新
   */
  async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.put(`/users/${id}`, userData);
  },

  /**
   * ユーザー削除
   */
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/users/${id}`);
  },

  /**
   * ユーザーステータス切替 (有効⇔無効)
   * PATCH /users/:id/toggle-status
   */
  async toggleUserStatus(id: string): Promise<ApiResponse<User>> {
    console.log('[User API] toggleUserStatus attempt', { id });
    return apiClient.patch(`/users/${id}/toggle-status`);
  }
};

// ===================================
// 車両関連API
// ===================================

export const vehicleAPI = {
  /**
   * 車両一覧取得
   */
  async getVehicles(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    return apiClient.get('/vehicles', { params });
  },

  /**
   * 車両作成
   */
  async createVehicle(vehicleData: any): Promise<ApiResponse<any>> {
    return apiClient.post('/vehicles', vehicleData);
  },

  /**
   * 車両更新
   */
  async updateVehicle(id: string, vehicleData: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/vehicles/${id}`, vehicleData);
  },

  /**
   * 車両削除
   */
  async deleteVehicle(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/vehicles/${id}`);
  },
  async hardDeleteVehicle(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/vehicles/${id}?hardDelete=true`);
  }
};

// ===================================
// ヘルスチェックAPI
// ===================================

export const healthAPI = {
  /**
   * ヘルスチェック
   */
  async check(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return apiClient.get('/health');
  }
};

// ===================================
// 点検項目関連API
// ===================================

export const inspectionItemAPI = {
  /**
   * 点検項目一覧取得
   */
  async getInspectionItems(params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/inspection-items', { params });
  },

  /**
   * 点検項目作成
   */
  async createInspectionItem(itemData: any): Promise<ApiResponse<any>> {
    return apiClient.post('/inspection-items', itemData);
  },

  /**
   * 点検項目更新
   */
  async updateInspectionItem(id: string, itemData: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/inspection-items/${id}`, itemData);
  },

  /**
   * 点検項目削除
   */
  async deleteInspectionItem(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/inspection-items/${id}`);
  },

  /**
   * 表示順更新
   */
  async updateOrder(items: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    return apiClient.post('/inspection-items/update-order', { items });
  }
};

// ===================================
// 場所関連API
// ===================================

export const locationAPI = {
  /**
   * 場所一覧取得
   */
  async getLocations(params?: any): Promise<ApiResponse<any>> {
    // ✅ FB-FvBuKFy0: 場所マスタ全件取得 - バックエンドMAX_PAGE_SIZE=100内で最大取得
    // バックエンド locationController は pageSize上限を bypass して全件取得する
    const merged = { limit: 100, ...params };
    return apiClient.get('/locations', { params: merged });
  },

  /**
   * 場所作成
   */
  async createLocation(locationData: any): Promise<ApiResponse<any>> {
    return apiClient.post('/locations', locationData);
  },

  /**
   * 場所更新
   */
  async updateLocation(id: string, locationData: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/locations/${id}`, locationData);
  },

  /**
   * 場所削除
   */
  async deleteLocation(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/locations/${id}`);
  },

  /**
   * 🆕 場所統合（重複場所メンテナンス）
   * 統合元の場所に紐づく運行記録を統合先へ付け替え、統合元は無効化する。
   */
  async mergeLocations(targetId: string, sourceLocationIds: string[]): Promise<ApiResponse<any>> {
    return apiClient.post(`/locations/${targetId}/merge`, { sourceLocationIds });
  }
};

// ===================================
// 品目関連API
// ===================================

export const itemAPI = {
  /**
   * 品目一覧取得
   */
  async getItems(params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/items', { params });
  },

  /**
   * 品目作成
   */
  async createItem(cargoData: any): Promise<ApiResponse<any>> {
    return apiClient.post('/items', cargoData);
  },

  /**
   * 品目更新
   */
  async updateItem(id: string, cargoData: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/items/${id}`, cargoData);
  },

  /**
   * 品目削除
   */
  async deleteItem(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/items/${id}`);
  },

  /**
   * 表示順更新
   */
  async updateOrder(items: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    return apiClient.post('/items/update-order', { items });
  }
};

// ===================================
// 運行記録関連API
// ===================================

export const operationAPI = {
  /**
   * 運行記録一覧取得
   */
  async getOperations(params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/operations', { params });
  },

  /**
   * 運行記録作成
   */
  async createOperation(operationData: any): Promise<ApiResponse<any>> {
    return apiClient.post('/operations', operationData);
  },

  /**
   * 運行記録更新
   */
  async updateOperation(id: string, operationData: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/operations/${id}`, operationData);
  },

  /**
   * 運行記録削除
   */
  async deleteOperation(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/operations/${id}`);
  },

  /**
   * 運行記録詳細取得
   */
  async getOperation(id: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/operations/${id}`);
  },

  /**
   * 運行記録ステータス更新
   */
  async updateStatus(id: string, status: string): Promise<ApiResponse<any>> {
    return apiClient.patch(`/operations/${id}/status`, { status });
  }
};

// ===================================
// GPS関連API
// ===================================

export const gpsAPI = {
  /**
   * GPS位置情報取得
   */
  async getGpsLocations(params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/gps/locations', { params });
  },

  /**
   * リアルタイム位置情報取得
   */
  async getRealTimeLocation(vehicleId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/gps/vehicles/${vehicleId}/location`);
  },

  /**
   * GPS履歴取得
   */
  async getGpsHistory(vehicleId: string, params?: any): Promise<ApiResponse<any>> {
    return apiClient.get(`/gps/vehicles/${vehicleId}/history`, { params });
  },

  /**
   * GPS設定更新
   */
  async updateGpsSettings(vehicleId: string, settings: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/gps/vehicles/${vehicleId}/settings`, settings);
  }
};

// ===================================
// レポート関連API
// ===================================

export const reportAPI = {
  /**
   * 日報取得
   */
  async getDailyReport(date: string, params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/reports/daily', { params: { date, ...params } });
  },

  /**
   * 月報取得
   */
  async getMonthlyReport(year: number, month: number, params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/reports/monthly', { params: { year, month, ...params } });
  },

  /**
   * 年報取得
   */
  async getAnnualReport(year: number, params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/reports/annual', { params: { year, ...params } });
  },

  /**
   * カスタムレポート取得
   */
  async getCustomReport(filters: any): Promise<ApiResponse<any>> {
    return apiClient.post('/reports/custom', filters);
  },

  /**
   * PDF出力
   */
  async exportPdf(reportType: string, params: any): Promise<ApiResponse<any>> {
    return apiClient.post(`/reports/export/pdf`, { reportType, ...params }, {
      responseType: 'blob'
    });
  },

  /**
   * Excel出力
   */
  async exportExcel(reportType: string, params: any): Promise<ApiResponse<any>> {
    return apiClient.post(`/reports/export/excel`, { reportType, ...params }, {
      responseType: 'blob'
    });
  }
};

// ===================================
// デバッグAPI（管理者専用）
// ===================================

export const debugAPI = {
  /**
   * 最近の運行ID一覧取得
   */
  async getRecentOperations(params?: { limit?: number }): Promise<ApiResponse<any>> {
    console.log('[Debug API] Get recent operations', params);
    return apiClient.get('/debug/operations/recent', { params });
  },

  /**
   * 点検項目詳細取得
   */
  async getInspectionItems(operationId: string): Promise<ApiResponse<any>> {
    console.log('[Debug API] Get inspection items', { operationId });
    return apiClient.get(`/debug/operations/${operationId}/inspection-items`);
  },

  /**
   * 運行・点検統合詳細取得
   */
  async getOperationDetail(operationId: string): Promise<ApiResponse<any>> {
    console.log('[Debug API] Get operation detail', { operationId });
    return apiClient.get(`/debug/operations/${operationId}/detail`);
  },

  /**
   * 運行履歴完全デバッグ情報取得
   */
  async getOperationDebugInfo(operationId: string): Promise<ApiResponse<any>> {
    console.log('[Debug API] Get operation debug info', { operationId });
    return apiClient.get(`/debug/operations/${operationId}/full`);
  },

  /**
   * エクスポートファイル一覧取得（backend/exports/ 配下の一括出力ファイル）
   */
  async getExportFiles(): Promise<ApiResponse<any>> {
    return apiClient.get('/debug/exports');
  }
};

// デフォルトエクスポート
export default api;

// =====================================
// 🆕 客先マスタ API
// =====================================
export const customerAPI = {
  async getCustomers(params?: any): Promise<ApiResponse<any>> {
    return apiClient.get('/customers', { params });
  },
  async getCustomer(id: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/customers/${id}`);
  },
  async createCustomer(data: any): Promise<ApiResponse<any>> {
    return apiClient.post('/customers', data);
  },
  async updateCustomer(id: string, data: any): Promise<ApiResponse<any>> {
    return apiClient.put(`/customers/${id}`, data);
  },
  async deleteCustomer(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/customers/${id}`);
  },
};
